import React, { useState, useEffect, useRef, memo } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useApi } from "../../../services/useApi";
import { ApiSuccessResponse } from "../../../types/api";
import DataTable from "../../../components/common/DataTable";
import MappingCell from "../components/MappingCell";
import MappingDialogs from "../components/MappingDialogs";
import { deepClean } from "../../../utils/objectUtils";
import { useCommonOptions } from '../../../hooks/useCommonOptions';
import SubmissionReportDialog, { SubmissionReport, BatchError } from '../components/SubmissionReportDialog';
import { useDynamicMappings } from '../../../hooks/useDynamicMappings';
import ValueCell from "../components/ValueCell";
import OneSnackbar from "../components/OneSnackbar";
import SubmitOptions from "../components/SubmitOptions";
import {UpdateIfExistsMap, BatchRecordError, BatchResponse,Pagination, MappingSubmitResponse} from '../../,,/../../interfaces/CommonInterface';



interface DineroAccount {
  accountNumber: string;
  publicStandardNumber?: string;
  name: string;
  vatCode: string;
  category: string;
  categoryName: string;
  isHidden: string;
  isDefaultSalesAccount: string;
}




interface EconomicAccount {
  dineroGuid?: string;
  isBarred: string;
  isBlockedForDirectEntries: string;
  isCredit: string;
  isDepartmentMandatory: string;
  isUnitMandatory: string;
  number: string;

  type: string;
  typeOriginal?: string;

  assetGroupNumber?: string;
  contraAccountNumber?: string;
  currency?: string;
  displayNumber?: string;
  keyFigureCodeNumber?: string;
  name?: string;
  openingAccountNumber?: string;
  realisationAccountNumber?: string;
  totalFromAccountNumber?: string;
  vatAccountNumber?: string;
  vatCode?: string;
}

interface AccountingPanelProps {
}

interface Mapping{
  isBarred: string;
  isBlockedForDirectEntries: string;
  isCredit: string;
  isDepartmentMandatory: string;
  isUnitMandatory: string;
  number: string;
  type: string;
  assetGroupNumber?: string;
  contraAccountNumber?: string;
  currency?: string;
  displayNumber?: string;
  keyFigureCodeNumber?: string;
  name?: string;
  openingAccountNumber?: string;
  realisationAccountNumber?: string;
  totalFromAccountNumber?: string;
  vatAccountNumber?: string;
  vatCode?: string;
}

interface DefaultValues extends Mapping {}


const AccountingPanel: React.FC<AccountingPanelProps> = () => {
  const defaultTemplate: Mapping = {
    isBarred: "",
    isBlockedForDirectEntries: "",
    isCredit: "",
    isDepartmentMandatory: "",
    isUnitMandatory: "",
    number: "accountNumber",
    type: "category",
    assetGroupNumber: "",
    contraAccountNumber: "",
    currency: "",
    displayNumber: "",
    keyFigureCodeNumber: "",
    name: "name",
    vatCode: "vatCode",
  };
  const handleLoadDataDinero = (filters: {
    fromDate: Date;
    toDate: Date;
  }) => {
    console.log('Loading entry data with filters:', filters);
  };


  const [dineroAccounts, setDineroAccounts] = useState<DineroAccount[]>([]);
  const [economicAccounts, setEconomicAccounts] = useState<EconomicAccount[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [economicLoading, setEconomicLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | string[] | null>(null);
  const [success, setSuccess] = useState<string | null>(null);  
  const [mapping, setMapping] = useState<Mapping>(defaultTemplate);
  const [batchSize] = useState(10);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    isBarred: "",
    isBlockedForDirectEntries: "",
    isCredit: "",
    isDepartmentMandatory: "",
    isUnitMandatory: "",
    number: "",
    type: "",
    assetGroupNumber: "",
    contraAccountNumber: "",
    currency: "",
    displayNumber: "",
    keyFigureCodeNumber: "",
  });
  const [requiredFields] = useState<Array<keyof Mapping>>([
    "isBarred",
    "isBlockedForDirectEntries",
    "isCredit",
    "isDepartmentMandatory",
    "isUnitMandatory",
    "number",
    "type"
  ]);
  const [updateKey, setUpdateKey] = useState<string>('');
  const [showReport, setShowReport] = useState(false);
  const [submissionReport, setSubmissionReport] = useState<SubmissionReport | null>(null);
  const [errorRecords, setErrorRecords] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);
  const [updateIfExistsMap, setUpdateIfExistsMap] = useState<UpdateIfExistsMap>({});

  const api = useApi();
  const { isLoading: isLoadingOptions,accountTypeOptions,yesNoOptions,currencies,vatCodes } = useCommonOptions();
  const { getMappedValue: getDynamicMappedValue} = useDynamicMappings();

  const [sequenceInfo, setSequenceInfo] = useState<{ 
    prefix: string; 
    startNumber: number;
    numberLength: number;
  } | null>(null);
  const [currentField, setCurrentField] = useState<string | null>(null);


  const handleUpdateData = () => {
    mapDineroToEconomic();
  };


  const handleLoadData = async () => {
    // if (!fromDate || !toDate) {
    //   setError("Please select both From Date and To Date");
    //   return;
    // }

    setLoading(true);
    setEconomicLoading(true);
    setError(null);
    setErrorRecords(new Set()); // Reset error records
    setShowReport(false);
    try {
      // handleLoadDataDinero({
      //   fromDate: fromDate,
      //   toDate: toDate
      // });


      const dineroResponse = await api.get<ApiSuccessResponse<DineroAccount[]>>(
        "/dinero/accounts",
        {
          params: {
          }
        }
      );
      if (dineroResponse.data.success) {
        setDineroAccounts(dineroResponse.data.data);
        setTotalCount(dineroResponse.data.data.length);
        // Map data from Dinero to e-conomic
        mapDineroToEconomic(dineroResponse.data.data);
      }
    } catch (error) {
      console.error("Error loading entry data:", error);
      if (!error.message.includes("No connection info")) {
        setError("Failed to load entry data. Please try again later.");
      }
    } finally {
      setLoading(false);
      setEconomicLoading(false);
    }
  };


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getDefaultValue = (field: keyof DefaultValues) => {
    return defaultValues[field];
  };

  const mapDineroToEconomic = (resDineroAccounts?: DineroAccount[]) => {
    const dineroAccountData = resDineroAccounts || dineroAccounts;
    const mappedEntries = dineroAccountData.map((item,index) => {
      function getMappedValue(field: string,alias?:string): { mapped: string | null, original: string | null } {
        const def = getDefaultValue(field as keyof DefaultValues);
        if (def) return { mapped: def, original: null };
        if (field in mapping) {     
          const key = mapping[field];
          if (key && key in item) {
            const raw = item[key as keyof typeof item];
            let newField = alias ? alias : field;
            const mapped = getDynamicMappedValue(newField, raw);
            return { mapped, original: raw };
          }
        }
      
        return { mapped: null, original: null };
      }
      const { mapped: accountType, original: accountTypeOriginal } = getMappedValue("type","accountType");
      // console.log(accountType,accountTypeOriginal);
      return {
        dineroGuid: item.accountNumber,
        isBarred: getMappedValue("isBarred").mapped,
        isBlockedForDirectEntries: getMappedValue("isBlockedForDirectEntries").mapped,
        isCredit: getMappedValue("isCredit").mapped,
        isDepartmentMandatory: getMappedValue("isDepartmentMandatory").mapped,
        isUnitMandatory: getMappedValue("isUnitMandatory").mapped,
        number: getMappedValue("number").mapped,
        type: accountType,
        typeOriginal: accountTypeOriginal,
        assetGroupNumber: getMappedValue("assetGroupNumber").mapped,
        contraAccountNumber: getMappedValue("contraAccountNumber").mapped,
        currency: getMappedValue("currency").mapped,
        displayNumber: getMappedValue("displayNumber").mapped,
        keyFigureCodeNumber: getMappedValue("keyFigureCodeNumber").mapped,
        name: getMappedValue("name").mapped,
        openingAccountNumber: getMappedValue("openingAccountNumber").mapped,
        realisationAccountNumber: getMappedValue("realisationAccountNumber").mapped,
        totalFromAccountNumber: getMappedValue("totalFromAccountNumber").mapped,
        vatAccountNumber: getMappedValue("vatAccountNumber").mapped,
        vatCode: getMappedValue("vatCode").mapped
      };
    });
    setEconomicAccounts(mappedEntries);
    setSuccess("Successfully mapped " + economicAccounts.length + " accounts");  
  };

  const validateMapping = (): string[] => {
    const errors: string[] = [];
    const uniqueFields = new Set(requiredFields);
    
    uniqueFields.forEach(field => {
      const mappingValue = mapping[field];
      const defaultValue = defaultValues[field];
      
      if (!mappingValue && !defaultValue) {
        const fieldName = field.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
        errors.push(`Field "${fieldName}" is required. Please map a field or set a default value.`);
      }
    });

    return errors;
  };

  const handleUpdateIfExistsChange = (field: string, value: boolean) => {
    setUpdateIfExistsMap(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitMapping = async () => {
    // Validate required fields
    const validationErrors = validateMapping();
    if (validationErrors.length > 0) {
      setError(validationErrors);
      return;
    }

    // if (updateExistingProduct && !updateKey) {
    //   setError("Please select a key for updating existing products");
    //   return;
    // }

    setIsSubmitting(true);
    setSubmitProgress(0);
    setError(null);
    setSuccess(null);
    setErrorRecords(new Set()); // Reset error records
    setShouldStop(false); // Reset stop flag
    setProcessedCount(0); // Reset processed count

    // Create new AbortController for this submission
    abortControllerRef.current = new AbortController();

    const totalRecords = economicAccounts.length;
    const batches = Math.ceil(totalRecords / batchSize);
    setTotalBatches(batches);

    let successCount = 0;
    let failedCount = 0;
    let allErrors: (string | BatchRecordError)[] = [];

    try {
      for (let i = 0; i < batches; i++) {
        // Check if should stop
        if (shouldStop) {
          break;
        }

        const start = i * batchSize;
        const end = Math.min(start + batchSize, totalRecords);
        const batchItems = economicAccounts.slice(start, end);

        const filteredBatch = batchItems
          .map(item => deepClean(item))
          .filter(item => item && Object.keys(item).length > 0);

        const response = await api.post<MappingSubmitResponse>(
          "/main/create-account",
          {
            mapping,
            accounts: filteredBatch,
            batchInfo: {
              currentBatch: i + 1,
              totalBatches: batches,
            },
            updateIfExists: updateIfExistsMap
          },
          {
            signal: abortControllerRef.current.signal
          }
        );

        const batchResponse = response.data.data;
        successCount += batchResponse.successCount;
        failedCount += batchResponse.failedCount;
        setProcessedCount(successCount); // Update processed count

        if (batchResponse.errors && batchResponse.errors.length > 0) {
          allErrors = [...allErrors, ...batchResponse.errors];
          batchResponse.errors.forEach(error => {
            setErrorRecords(prev => new Set(prev).add(error.recordId));
          });
        }

        setCurrentBatch(i + 1);
        setSubmitProgress(Math.round(((i + 1) / batches) * 100));
      }

      // Only process errors and show report if not stopped
      if (!shouldStop) {
        const reportErrors: (string | BatchError)[] = [];
        allErrors.forEach(error => {
          if (typeof error === 'string') {
            reportErrors.push(error);
          } else {
            error.errors.forEach(err => {
              reportErrors.push({
                recordId: error.recordId,
                field: '',
                message: err,
                value: ''
              });
            });
          }
        });

        setSubmissionReport({
          successCount,
          failedCount,
          errors: reportErrors
        });
        setShowReport(true);

        if (reportErrors.length > 0) {
          setError(
            `Failed to process ${failedCount} records. Click "View Report" for details.`
          );
        } else {
          const successMessage = `Successfully processed ${successCount} records.`;
          setSuccess(successMessage);
        }
      }
    } catch (error) {

      if (error.status === 500 && error.message === 'canceled') {
         setShowReport(true);
         setShouldStop(false);
        // Don't show message here as it's already shown in handleStopSubmit
      } else {
        console.error("Error submitting mapping:", error);
        setError("Failed to submit mapping. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
      abortControllerRef.current = null;
      console.log('finally');
    }
  };

  const handleStopSubmit = () => {
    setShouldStop(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Show stop message immediately
    setSuccess(`Process stopped. Successfully processed ${processedCount} records.`);
    setIsSubmitting(false);
    setSubmitProgress(0);
    abortControllerRef.current = null;
  };

  const dineroColumns = [
    { id: "accountNumber", label: "Account Number" },
    {id: "publicStandardNumber", label:"Public Standard Number"},
    {id: "name", label:"Account Name"},
    {id: "vatCode", label:"VAT Code"},
    {id: "category", label:"Category"},
    {id: "categoryName", label:"Category Name"},
    {id: "isHidden", label:"Is Hidden"},
    {id: "isDefaultSalesAccount", label:"Is Default Sales Account"},
  ];

  const scrollToErrorRow = (recordId: string) => {
    // Find the index of the contact with the matching recordId
    const productIndex = economicAccounts.findIndex(account => account.dineroGuid === recordId);
    if (productIndex === -1) return;

    // Calculate which page the contact is on
    const newPage = Math.floor(productIndex / rowsPerPage);
    
    // If the contact is not on the current page, change the page
    if (newPage !== page) {
      setPage(newPage);
    }

    // Wait for the page to update and then scroll to the row
    setTimeout(() => {
      const row = document.getElementById(`row-${recordId}`);
      if (row && tableRef.current) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the row temporarily
        row.style.backgroundColor = 'rgba(255, 0, 0, 0.3)';
        setTimeout(() => {
          row.style.backgroundColor = '';
        }, 2000);
      }
    }, 100); // Small delay to allow page update
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
          Filter Accounts from Dinero
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              slotProps={{ 
                textField: { size: "small" }
              }}
              format="dd/MM/yyyy"
            />
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              slotProps={{ 
                textField: { size: "small" }
              }}
              format="dd/MM/yyyy"
            />
          </LocalizationProvider> */}
       
          <Button
            variant="contained"
            onClick={handleLoadData}
            sx={{ minWidth: 120 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Load Data"}
          </Button>
        </Box>
      </Paper>
      <MappingDialogs
          mapping={mapping}
          defaultValues={defaultValues}
          type="accounting"
          onApplyMapping={(newMap, defaults) => {
            setMapping(newMap);
            setDefaultValues(defaults);
          }}
          onLoadDefaultTemplate = {()=>{
            setMapping(defaultTemplate);
          }}
          
          onSuccess={(msg) => setSuccess(msg)}
      />

      
    <SubmitOptions
        mapDineroToEconomic={handleUpdateData}
        dineroData={dineroAccounts}
        economicData={economicAccounts}
        isSubmitting={isSubmitting}
        handleSubmitMapping={handleSubmitMapping}
        handleStopSubmit={handleStopSubmit}
        submissionReport={submissionReport}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        submitProgress={submitProgress}
        setShowReport={setShowReport}
      />

      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
      >
        Dinero Accounts
      </Typography>

      <Box
        sx={{
          "& table": {
            tableLayout: "auto", // 👈 Cho phép tự động tính width
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            fontSize: "0.75rem",
            fontWeight: "bold",
            width: "auto", // 👈 Cho phép tự co giãn
            whiteSpace: "nowrap", // 👈 Tránh xuống dòng nếu muốn giữ nguyên 1 dòng
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
          },
        }}
      >
        <DataTable
          columns={dineroColumns}
          data={dineroAccounts}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          maxHeight={600}
          size="small"
        />
      </Box>

      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main", mt:2 }}
      >
          E-conomic Accounts
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }} ref={tableRef}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            "& th, & td": {
              whiteSpace: "nowrap",
            },
          }}
          aria-label="economic customer table"
        >
          <TableHead>
            <TableRow>
            <MappingCell
                key="isBarred"
                label="Is Barred"
                field="isBarred"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
                itemOptions={yesNoOptions}
                />
                <MappingCell
                key="isBlockedForDirectEntries"
                label="Is Blocked For Direct Entries"
                field="isBlockedForDirectEntries"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                itemOptions={yesNoOptions}
                />
                <MappingCell
                key="isCredit"
                label="Is Credit"
                field="isCredit"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                itemOptions={yesNoOptions}
                />  
                <MappingCell
                key="isDepartmentMandatory"
                label="Is Department Mandatory"
                field="isDepartmentMandatory"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                itemOptions={yesNoOptions}
                />  
                <MappingCell
                key="isUnitMandatory"
                label="Is Unit Mandatory"
                field="isUnitMandatory"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                itemOptions={yesNoOptions}
                />  
                <MappingCell
                key="number"
                label="Number"
                field="number"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                  options={dineroColumns}
                requiredFields={requiredFields}   
                />  
                <MappingCell
                key="type"
                label="Type"
                field="type"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                itemOptions={accountTypeOptions}
                />
                {/* <MappingCell
                key="assetGroupNumber"
                label="Asset Group Number"
                field="assetGroupNumber"
                mapping={mapping} 
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}    
                /> */}
                <MappingCell  
                key="contraAccountNumber"
                label="Contra Account Number"
                field="contraAccountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />
                <MappingCell
                key="currency"
                label="Currency"    
                field="currency"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                itemOptions={currencies}
                />
                {/* <MappingCell
                key="displayNumber"
                label="Display Number"
                field="displayNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />     */}
                <MappingCell
                key="keyFigureCodeNumber"
                label="Key Figure Code Number"
                field="keyFigureCodeNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />    
                <MappingCell
                key="name"
                label="Name"
                field="name"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />    
                {/* <MappingCell
                key="openingAccountNumber"
                label="Opening Account Number"
                field="openingAccountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />     */}
                {/* <MappingCell
                key="realisationAccountNumber"
                label="Realisation Account Number"
                field="realisationAccountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />  
                <MappingCell
                key="totalFromAccountNumber"
                label="Total From Account Number"
                field="totalFromAccountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />       */}
                {/* <MappingCell
                key="vatAccountNumber"
                label="Vat Account Number"
                field="vatAccountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                />     */}
                <MappingCell
                key="vatCode"
                label="Vat Code"
                field="vatCode"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}   
                itemOptions={vatCodes}
                />  
                  
                
                
                 
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={20} align="center" sx={{ padding: "8px" }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : economicAccounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={20} align="center" sx={{ padding: "8px" }}>
                  <Typography variant="body2">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              economicAccounts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((account) => {
                  const hasError = errorRecords.has(account.dineroGuid);
                  return (
                    <TableRow 
                      key={account.dineroGuid}
                      id={`row-${account.dineroGuid}`}
                      sx={{
                        backgroundColor: hasError ? 'rgba(255, 0, 0, 0.1)' : undefined,
                        '&:hover': {
                          backgroundColor: hasError ? 'rgba(255, 0, 0, 0.2)' : undefined
                        }
                      }}
                    >
                      <ValueCell value={account.isBarred} options={yesNoOptions}/>
                      <ValueCell value={account.isBlockedForDirectEntries} options={yesNoOptions}/>
                      <ValueCell value={account.isCredit} options={yesNoOptions}/>
                      <ValueCell value={account.isDepartmentMandatory} options={yesNoOptions}/>
                      <ValueCell value={account.isUnitMandatory} options={yesNoOptions}/>
                      <ValueCell value={account.number} />
                      <ValueCell value={account.type} options={accountTypeOptions} originalValue={account.typeOriginal}/>
                      {/* <ValueCell value={account.assetGroupNumber} /> */}
                      <ValueCell value={account.contraAccountNumber} />
                      <ValueCell value={account.currency} options={currencies}/>
                      {/* <ValueCell value={account.displayNumber} /> */}
                      <ValueCell value={account.keyFigureCodeNumber} />
                      <ValueCell value={account.name} />
                      {/* <ValueCell value={account.openingAccountNumber} /> */}
                      {/* <ValueCell value={account.realisationAccountNumber} /> */}
                      {/* <ValueCell value={account.totalFromAccountNumber} /> */}
                      {/* <ValueCell value={account.vatAccountNumber} /> */}
                      <ValueCell value={account.vatCode} options={vatCodes} />
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            zIndex: 1,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem",
              },
          }}
        />
      </TableContainer>
      
      <OneSnackbar
        error={error}
        success={success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      />

      <SubmissionReportDialog
        open={showReport}
        onClose={() => setShowReport(false)}
        report={submissionReport}
        onErrorClick={scrollToErrorRow}
      />
    </>
  );
};

export default memo(AccountingPanel);
